// src/features/markets/marketsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toChartKeyLevels } from './keyLevels';
import { toChartFib } from './cycleFib';
import { getIndicatorEventDefinitions, getEventLookbackDays } from '../../utils/indicatorEvents';
import apiClient from '../../api/apiClient';
import { computeTrendlinesForChart } from '../../utils/trendlines';
import { candleCache } from '../../utils/candleCache';
import { mergeCandles } from '../../utils/candleMerging';
import { resolveTimestampFromCandle } from '../../utils/candleUtils';

// V1 is the only served trendline version (DWLF-322 retired V2). Every trendline
// request uses it regardless of what a caller passes, so a legacy `version: 2`
// can never reach the API.
export const DEFAULT_TRENDLINE_VERSION = 1;

const getSymbolVariants = (symbol) => {
  if (typeof symbol !== 'string' || !symbol) {
    return [];
  }
  const hyphenated = symbol.replace('/', '-');
  const slashed = symbol.replace('-', '/');
  return Array.from(new Set([symbol, hyphenated, slashed].filter(Boolean)));
};

export const normalizeSymbolForRequest = (symbol) => {
  if (typeof symbol !== 'string' || !symbol) {
    return '';
  }
  return symbol.replace('/', '-');
};

// resolveTimestampFromCandle imported from utils/candleUtils.js

/**
 * Local fallback for the server's trendline computation, used when a chart has candles but
 * the API did not return trendlines for them.
 *
 * JSDoc rather than inline types: this is a `.js` file, and an inline annotation here parses
 * under CRA's babel but under no linter or type checker, which silently exempts the whole
 * module from both.
 *
 * @param {unknown[]} candles
 * @param {string} timeframe
 * @param {{swingLookbacks?: Record<string, number>}} [options]
 */
const computeLocalTrendlinesFromCandles = (
  candles,
  timeframe,
  { swingLookbacks } = {},
) => {
  return computeTrendlinesForChart({
    candles,
    timeframe,
    swingLookbacks,
  });
};

const attachTimeframeToTrendlines = (trendlines, timeframe) => {
  if (!Array.isArray(trendlines)) {
    return [];
  }
  const normalized = typeof timeframe === 'string' ? timeframe : undefined;
  return trendlines.map((line) => {
    if (line?.timeframe || !normalized) {
      return line;
    }
    return {
      ...line,
      timeframe: normalized,
    };
  });
};

// Staleness thresholds (ms)
const FIVE_MINUTES = 5 * 60 * 1000;
const TWO_MINUTES = 2 * 60 * 1000;

// Fetch all available market symbols
export const fetchMarketSymbols = createAsyncThunk(
  'markets/fetchMarketSymbols',
  async (arg, thunkAPI) => {
    try {
      //const response = await apiClient.get('/market-data/symbols');
      //return response.data.symbols || [];

      const response = await apiClient.get('/symbols');
      const symbols = Array.isArray(response.data.symbols) ? response.data.symbols : [];

      return symbols
        .filter((item) => {
          const value = item?.isActive;
          return value === true || value === 'true' || value === 1 || value === '1';
        })
        .map((item) => ({ symbol: item.symbol, name: item.name }));
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch market symbols');
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { markets } = getState();
      if (markets.symbolsLastFetched && markets.symbolsLastFetched > Date.now() - FIVE_MINUTES) {
        return false;
      }
    },
  }
);

export const fetchAlertsByAsset = createAsyncThunk(
  'markets/fetchAlertsByAsset',
  async (symbol, thunkAPI) => {
    try {
      const response = await apiClient.get(`/alerts/asset/${normalizeSymbolForRequest(symbol)}`);
      const result = { symbol, alerts: response.data.alerts };
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch alerts by asset');
    }
  }
);

// The system fib (DWLF-334): the current cycle-fib leg per timeframe, computed
// nightly by dwlf-scheduled-jobs and served as stored. Null when the symbol has
// none, never a browser-side recomputation.
export const fetchSystemFibLevels = createAsyncThunk(
  'markets/fetchSystemFibLevels',
  async (symbol) => {
    try {
      const response = await apiClient.get(`/fib-levels/system/${normalizeSymbolForRequest(symbol)}`);
      return { symbol, level: toChartFib(response.data) };
    } catch (error) {
      // A failed read draws no fib, and is not a rejection: a missing fib must
      // not fail the page. Anything but a 404 is logged.
      if (error?.response?.status !== 404) {
        console.error('markets/fetchSystemFibLevels: failed to load the system fib', error);
      }
      return { symbol, level: null };
    }
  }
);

export const fetchRecentlyTriggeredAlerts = createAsyncThunk(
  'markets/fetchRecentlyTriggeredAlerts',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get('/alerts/recently-triggered');
      return response.data.alerts;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch recent alerts');
    }
  }
);

// ✅ Add asset to watchlist
export const addToWatchlist = createAsyncThunk(
  'markets/addToWatchlist',
  async (symbol, thunkAPI) => {
    try {
      const response = await apiClient.post('/watchlist', { symbol });
      return response.data.symbol;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to add to watchlist');
    }
  }
);

// ✅ Remove asset from watchlist
export const removeFromWatchlist = createAsyncThunk(
  'markets/removeFromWatchlist',
  async (symbol, thunkAPI) => {
    try {
      await apiClient.delete(`/watchlist/${normalizeSymbolForRequest(symbol)}`);
      return symbol;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to remove from watchlist');
    }
  }
);

// Fetch user's watchlist
export const fetchWatchlist = createAsyncThunk(
  'markets/fetchWatchlist',
  async (arg, thunkAPI) => {
    try {
      const response = await apiClient.get('/watchlist');
      return response.data.watchlist;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch watchlist');
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { markets } = getState();
      // Skip if a fetch is already in flight (prevents duplicate dispatches
      // from page + widget mounting in the same render commit)
      if (markets.watchlistLoading) return false;
      if (markets.watchlistLastFetched && markets.watchlistLastFetched > Date.now() - TWO_MINUTES) {
        return false;
      }
    },
  }
);

// Load cached market data from IndexedDB for instant rendering
export const loadCachedMarketData = createAsyncThunk(
  'markets/loadCachedMarketData',
  async ({ symbol, timeframe = 'daily' }, thunkAPI) => {
    try {
      const cached = await candleCache.getCandles(symbol, timeframe);
      if (!cached || candleCache.isCacheStale(cached)) {
        return null; // No valid cache found
      }

      return {
        symbol,
        candles: cached.candles,
        timeframe: cached.timeframe,
        meta: cached.meta || {},
        isFromCache: true,
        cacheTimestamp: cached.timestamp
      };
    } catch (error) {
      console.warn('Failed to load cached market data:', error);
      return null;
    }
  }
);

// Load market data with cache-first strategy
export const loadMarketData = createAsyncThunk(
  'markets/loadMarketData',
  async (arg, thunkAPI) => {
    const { symbol, timeframe = 'daily', forceFullFetch = false } = 
      typeof arg === 'string' ? { symbol: arg, timeframe: 'daily' } : arg || {};

    if (!symbol) {
      throw new Error('Symbol is required');
    }

    let cachedResult = null;
    let hasCachedData = false;
    
    // 1. First, try to load from cache for instant rendering
    if (!forceFullFetch) {
      cachedResult = await thunkAPI.dispatch(loadCachedMarketData({ symbol, timeframe }));
      hasCachedData = !!(cachedResult.payload);
    }

    // 2. Then fetch fresh/incremental data from API
    const apiResult = await thunkAPI.dispatch(fetchMarketData(arg));
    
    // Check if the API fetch was rejected
    if (apiResult.type.endsWith('/rejected')) {
      // If we have cached data, return it with error info instead of rejecting
      if (hasCachedData) {
        return {
          ...cachedResult.payload,
          fromCacheOnly: true,
          apiError: apiResult.payload
        };
      }
      // No cached data available, re-throw the error
      return thunkAPI.rejectWithValue(apiResult.payload);
    }
    
    // Return the API result (which may be incremental)
    // The Redux reducer will handle merging with any existing cache data
    return {
      ...apiResult.payload,
      hadCachedData: hasCachedData
    };
  }
);

// What a full (non-incremental) fetch asks for when the caller gives no limit. Weekly asks for the endpoint's
// maximum (~20 years, DWLF-424); the endpoint's own 10-year default is left alone because the MCP
// get_market_data tool relies on it. A caller passing a smaller weekly limit replaces the cached weekly
// history with that shorter series.
export const FULL_FETCH_LIMITS = { weekly: 1040 };

// Fetch OHLC market data for a symbol
export const fetchMarketData = createAsyncThunk(
  'markets/fetchMarketData',
  async (arg, thunkAPI) => {
    try {
      const {
        symbol,
        timeframe = 'daily',
        limit = FULL_FETCH_LIMITS[timeframe],
        semiLive,
        forceFullFetch = false,
      } = typeof arg === 'string' ? { symbol: arg, timeframe: 'daily' } : arg || {};

      if (!symbol) {
        throw new Error('Symbol is required');
      }

      // Check for existing data to enable incremental loading
      const state = thunkAPI.getState();
      const existingEntry = state?.markets?.marketData?.[symbol];
      const existingCandles = existingEntry?.byTimeframe?.[timeframe];
      const lastKnownDate = existingEntry?.lastKnownDate?.[timeframe];
      
      // Determine if we should do incremental fetch
      const shouldUseIncremental = !forceFullFetch && 
                                   Array.isArray(existingCandles) && 
                                   existingCandles.length > 0 && 
                                   lastKnownDate;

      const params = new URLSearchParams();
      if (timeframe) {
        params.append('timeframe', timeframe);
      }
      if (limit && !shouldUseIncremental) {
        params.append('limit', String(limit));
      }
      
      // Add 'since' parameter for incremental loading
      if (shouldUseIncremental) {
        // Convert timestamp to ISO date string for backend
        const sinceDate = new Date(lastKnownDate).toISOString().split('T')[0];
        params.append('since', sinceDate);
      }
      
      // Default to requesting semi-live data unless explicitly disabled by argument or user setting.
      const userSetting = state?.userSettings?.settings?.semiLiveCandlesEnabled;
      const shouldRequestSemiLive =
        semiLive !== false && userSetting !== false;
      if (shouldRequestSemiLive) {
        params.append('semiLive', 'true');
      }

      // Convert '/' to '-' for URL path (e.g., BTC/USD -> BTC-USD), backend converts back
      const urlSymbol = normalizeSymbolForRequest(symbol);
      const url = `/market-data/${urlSymbol}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get(url);
      const { candles = [], timeframe: resolvedTimeframe = timeframe, ...rest } = response.data || {};

      // Save to IndexedDB cache for future instant loading
      try {
        if (shouldUseIncremental) {
          // Update existing cache with new incremental data
          await candleCache.updateCandles(symbol, resolvedTimeframe, candles);
        } else {
          // Store full candle set
          await candleCache.storeCandles(symbol, resolvedTimeframe, candles, rest);
        }
      } catch (error) {
        console.warn('Failed to cache market data:', error);
        // Don't fail the request if caching fails
      }

      return {
        symbol,
        candles,
        timeframe: resolvedTimeframe,
        meta: rest,
        isIncremental: shouldUseIncremental,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message || 'Failed to fetch market data');
    }
  }
);

// How many candles each step back through history asks for (DWLF-426). The endpoint returns the
// candles strictly older than `before`, and an empty page once nothing older is stored.
export const OLDER_PAGE_LIMITS = { daily: 1000, weekly: 520, hourly: 1000 };

const olderHistoryOf = (state, symbol, timeframe) =>
  state?.markets?.olderHistory?.[symbol]?.[timeframe] || {};

// Load the page of candles just before the oldest one held for symbol/timeframe, for when the
// chart is panned back to the start of what it has. One request at a time per series, and none
// once the start of history has been reached.
export const fetchOlderCandles = createAsyncThunk(
  'markets/fetchOlderCandles',
  async ({ symbol, timeframe }, thunkAPI) => {
    try {
      const held = thunkAPI.getState().markets.marketData[symbol].byTimeframe[timeframe];
      const oldest = resolveTimestampFromCandle(held[0]);
      const params = new URLSearchParams({
        timeframe,
        limit: String(OLDER_PAGE_LIMITS[timeframe] || 1000),
        before: new Date(oldest).toISOString(),
      });
      const urlSymbol = normalizeSymbolForRequest(symbol);
      const response = await apiClient.get(`/market-data/${urlSymbol}?${params.toString()}`);
      const candles = Array.isArray(response.data?.candles) ? response.data.candles : [];

      // Only extend a cached series: with nothing cached, updateCandles would store this old page
      // alone, and the next visit would resume `since` its last candle and skip everything newer.
      if (candles.length > 0) {
        try {
          if (await candleCache.getCandles(symbol, timeframe)) {
            await candleCache.updateCandles(symbol, timeframe, candles);
          }
        } catch (error) {
          console.warn('Failed to cache older market data:', error);
        }
      }

      return { symbol, timeframe, candles };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message || 'Failed to fetch older market data');
    }
  },
  {
    condition: ({ symbol, timeframe } = {}, { getState }) => {
      const state = getState();
      const held = state?.markets?.marketData?.[symbol]?.byTimeframe?.[timeframe];
      if (!Array.isArray(held) || held.length === 0) return false;
      if (resolveTimestampFromCandle(held[0]) === null) return false;
      const history = olderHistoryOf(state, symbol, timeframe);
      return !history.loading && !history.exhausted;
    },
  }
);

const selectMarketDataEntry = (state, symbol) => {
  if (!state || !state.markets) {
    return undefined;
  }

  const collection = state.markets.marketData || {};
  const variants = getSymbolVariants(symbol);

  for (const key of variants) {
    if (Object.prototype.hasOwnProperty.call(collection, key)) {
      return collection[key];
    }
  }

  return undefined;
};

const selectSwingLookbacks = (state) => state?.markets?.indicatorSettings?.swing || {};

const ensureCandlesForTimeframe = async (symbol, timeframe, thunkAPI) => {
  const state = thunkAPI.getState();
  const entry = selectMarketDataEntry(state, symbol);
  let candles = entry?.byTimeframe?.[timeframe];

  if (Array.isArray(candles) && candles.length > 0) {
    return candles;
  }

  const formattedSymbol = normalizeSymbolForRequest(symbol);
  if (!formattedSymbol) {
    return [];
  }

  const result = await thunkAPI
    .dispatch(fetchMarketData({ symbol: formattedSymbol, timeframe }))
    .unwrap();

  return Array.isArray(result?.candles) ? result.candles : [];
};

// Fetch details of a single symbol
export const fetchSymbolDetails = createAsyncThunk(
  'markets/fetchSymbolDetails',
  async (symbol, thunkAPI) => {
    try {
      const response = await apiClient.get(`/symbols/${normalizeSymbolForRequest(symbol)}`);
      return { symbol, details: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch symbol details');
    }
  }
);

// Key levels (DWLF-333): computed nightly server-side from daily candles and
// published cycle pivots — the same levels the API, MCP, alerts and strategy
// events use. One set per symbol, so it is stored under `daily` and the page's
// selector serves it on every timeframe. Each side is nearest-first.
export const fetchSupportResistance = createAsyncThunk(
  'markets/fetchSupportResistance',
  async (arg, { rejectWithValue }) => {
    const { symbol } = typeof arg === 'string' ? { symbol: arg } : (arg || {});
    if (!symbol) {
      return rejectWithValue('Symbol is required');
    }
    try {
      const response = await apiClient.get(`/support-resistance/${normalizeSymbolForRequest(symbol)}`);
      return { symbol, timeframe: 'daily', levels: toChartKeyLevels(response.data) };
    } catch (error) {
      // A symbol with no row yet (404) or a failed read draws no levels. Not a
      // rejection: the .rejected reducer sets the whole markets slice's status
      // to failed, and missing guides should not fail the page (the tracker
      // this replaces behaved the same).
      if (error?.response?.status !== 404) {
        console.error('markets/fetchSupportResistance: failed to load key levels', error);
      }
      return { symbol, timeframe: 'daily', levels: { support: [], resistance: [] } };
    }
  }
);

export const fetchTrendlines = createAsyncThunk(
  'markets/fetchTrendlines',
  async (arg, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    const {
      symbol,
      timeframe = 'daily',
    } = typeof arg === 'string' ? { symbol: arg, timeframe: 'daily' } : arg || {};

    if (!symbol) {
      return rejectWithValue('Symbol is required');
    }

    const normalizedTimeframe = typeof timeframe === 'string' ? timeframe.toLowerCase() : 'daily';
    const numericVersion = DEFAULT_TRENDLINE_VERSION;
    const swingLookbacks = selectSwingLookbacks(thunkAPI.getState());

    try {
      const candles = await ensureCandlesForTimeframe(symbol, normalizedTimeframe, thunkAPI);
      if (!Array.isArray(candles) || candles.length < 3) {
        return {
          symbol,
          version: numericVersion,
          timeframe: normalizedTimeframe,
          trendlines: [],
        };
      }

      const trendlines = computeLocalTrendlinesFromCandles(candles, normalizedTimeframe, {
        swingLookbacks,
      });
      return {
        symbol,
        version: numericVersion,
        timeframe: normalizedTimeframe,
        trendlines,
      };
    } catch (localError) {
      console.error('markets/fetchTrendlines: Failed to compute local trendlines, falling back to API', localError);
    }

    try {
      const response = await apiClient.get(`/trendlines/${normalizeSymbolForRequest(symbol)}`, {
        params: { version: numericVersion, timeframe: normalizedTimeframe },
      });

      const trendlines = attachTimeframeToTrendlines(response.data.trendlines, normalizedTimeframe);

      return {
        symbol,
        version: numericVersion,
        timeframe: normalizedTimeframe,
        trendlines,
      };
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

// Fetch all recent events with appropriate timeframes for different event types
export const fetchAllRecentEvents = createAsyncThunk(
  'markets/fetchAllRecentEvents',
  async (_, thunkAPI) => {
    try {
      const indicatorEventDefinitions = getIndicatorEventDefinitions();
      const eventTypes = indicatorEventDefinitions.map((definition) => ({
        type: definition.id,
        days: getEventLookbackDays(definition.id),
      }));

      if (eventTypes.length === 0) {
        return [];
      }

      // Fire all event-type requests in parallel. The previous
      // sequential-chunk approach (groups of 10, awaited serially)
      // added artificial latency equal to (N_chunks − 1) × slowest-
      // call-per-chunk. The backend handles the concurrency fine —
      // each request is a cheap DynamoDB query. Catching per-request
      // errors ensures one failed type doesn't block the rest.
      const allResults = await Promise.all(
        eventTypes.map(({ type, days }) =>
          apiClient
            .get(`/events?type=${type}&days=${days}&uniquePerSymbol=true`)
            .then((response) => response.data.events || [])
            .catch((error) => {
              console.error('markets/fetchAllRecentEvents: failed to fetch events', { type, days, error });
              return [];
            })
        ),
      );

      const allEvents = allResults.flat().sort((a, b) => new Date(b.date) - new Date(a.date));
      return allEvents;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch events');
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { markets } = getState();
      // Skip if already loading to prevent duplicate calls on initial mount
      if (markets.allRecentEventsStatus === 'loading') {
        return false;
      }
      // Skip if already fetched within last 2 minutes to avoid duplicate calls
      if (markets.allRecentEventsLastFetched && markets.allRecentEventsLastFetched > Date.now() - TWO_MINUTES) {
        return false;
      }
    },
  }
);

// Fetch recent trendline breaks
export const fetchRecentTrendlineBreaks = createAsyncThunk(
  'markets/fetchRecentTrendlineBreaks',
  async (_, thunkAPI) => {
    try {
      const trendlineBreakTypes = [
        'trendline_break_bullish',
        'trendline_break_bearish',
        'trendline_break',
      ];

      const results = await Promise.all(
        trendlineBreakTypes.map((type) =>
          apiClient
            .get(`/events?days=3&type=${type}&uniquePerSymbol=true`)
            .then((response) => response.data.events || [])
            .catch((error) => {
              console.error('markets/fetchRecentTrendlineBreaks: failed to fetch events', { type, error });
              return [];
            }),
        ),
      );

      const seen = new Set();
      const combined = [];

      results.flat().forEach((event) => {
        if (!event) {
          return;
        }
        const key = `${event.symbol || 'unknown'}_${event.eventType || 'unknown'}_${event.date || 'unknown'}`;
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        combined.push(event);
      });

      combined.sort((a, b) => new Date(b.date) - new Date(a.date));
      return combined;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch trendline breaks');
    }
  }
);

// Fetch swing highs & lows for a symbol
export const fetchSwingPoints = createAsyncThunk(
  'markets/fetchSwingPoints',
  async (arg, thunkAPI) => {
    try {
      const {
        symbol,
        timeframe = 'daily',
        type,
      } = typeof arg === 'string' ? { symbol: arg, timeframe: 'daily' } : arg || {};

      if (!symbol) {
        throw new Error('Symbol is required');
      }

      const params = new URLSearchParams();
      if (timeframe) {
        params.append('timeframe', timeframe);
      }
      if (type) {
        params.append('type', type);
      }

      const response = await apiClient.get(`/swings/${normalizeSymbolForRequest(symbol)}${params.toString() ? `?${params.toString()}` : ''}`);
      return { symbol, timeframe, swings: response.data.swings };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch swing points');
    }
  }
);

// Fetch market performance summary
// Combined markets overview — fetches tags + performance summary +
// watchlist in one API call via GET /v2/markets/overview. Replaces
// three separate round trips on the markets dashboard first load.
export const fetchMarketsOverview = createAsyncThunk(
  'markets/fetchMarketsOverview',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get('/markets/overview');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch markets overview');
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { markets } = getState();
      if (markets.marketsOverviewLoading) return false;
      // Use a dedicated timestamp so the overview condition is
      // decoupled from the standalone fetchPerformanceSummary thunk.
      if (
        markets.marketsOverviewLastFetched
        && markets.marketsOverviewLastFetched > Date.now() - TWO_MINUTES
      ) {
        return false;
      }
    },
  },
);

export const fetchPerformanceSummary = createAsyncThunk(
  'markets/fetchPerformanceSummary',
  async (arg, thunkAPI) => {
    try {
      const response = await apiClient.get('/performance-summary');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch performance summary');
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { markets } = getState();
      // Skip if a fetch is already in flight (prevents duplicate dispatches
      // from page + widget mounting in the same render commit)
      if (markets.performanceSummaryLoading) return false;
      if (markets.performanceSummary && markets.performanceSummaryLastFetched > Date.now() - TWO_MINUTES) {
        return false;
      }
    },
  }
);

// Fetch weekly trendlines for a given symbol
export const fetchWeeklyTrendlines = createAsyncThunk(
  'markets/fetchWeeklyTrendlines',
  async (arg, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    const { symbol } = typeof arg === 'string' ? { symbol: arg } : arg || {};

    if (!symbol) {
      return rejectWithValue('Symbol is required');
    }

    const numericVersion = DEFAULT_TRENDLINE_VERSION;
    const timeframe = 'weekly';
    const swingLookbacks = selectSwingLookbacks(thunkAPI.getState());

    try {
      const candles = await ensureCandlesForTimeframe(symbol, timeframe, thunkAPI);
      if (!Array.isArray(candles) || candles.length < 3) {
        return { symbol, version: numericVersion, timeframe, trendlines: [] };
      }
      const trendlines = computeLocalTrendlinesFromCandles(candles, timeframe, {
        swingLookbacks,
      });
      return { symbol, version: numericVersion, timeframe, trendlines };
    } catch (localError) {
      console.error(
        'markets/fetchWeeklyTrendlines: Failed to compute local weekly trendlines, falling back to API',
        localError,
      );
    }

    try {
      const response = await apiClient.get(`/trendlines/weekly/${normalizeSymbolForRequest(symbol)}`, {
        params: { version: numericVersion },
      });
      const trendlines = attachTimeframeToTrendlines(response.data.trendlines, timeframe);
      return {
        symbol,
        version: numericVersion,
        timeframe,
        trendlines,
      };
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

// Load the page of candles before the oldest one held, then redraw that timeframe's trendlines over
// the longer series, since they are computed once from the first load (DWLF-434). `trendlineSymbol` is
// the key the page stores trendlines under. Every page recomputes over the whole merged series.
export const loadOlderHistory = createAsyncThunk(
  'markets/loadOlderHistory',
  async ({ symbol, trendlineSymbol, timeframe }, { dispatch, getState }) => {
    const result = await dispatch(fetchOlderCandles({ symbol, timeframe }));
    if (!fetchOlderCandles.fulfilled.match(result) || result.payload.candles.length === 0 || !trendlineSymbol) {
      return;
    }
    // Not after a store reset (sign-out) mid-request: the trendline thunks would load the series again.
    if (!getState().markets.marketData[symbol]?.byTimeframe?.[timeframe]?.length) {
      return;
    }
    if (timeframe === 'weekly') {
      await dispatch(fetchWeeklyTrendlines({ symbol: trendlineSymbol }));
    } else {
      await dispatch(fetchTrendlines({ symbol: trendlineSymbol, timeframe }));
    }
  }
);

// Fetch weekly swing highs & lows for a symbol
export const fetchWeeklySwingPoints = createAsyncThunk(
  'markets/fetchWeeklySwingPoints',
  async (symbol, thunkAPI) => {
    try {
      const response = await apiClient.get(`/swings/weekly/${normalizeSymbolForRequest(symbol)}`);
      return { symbol, swings: response.data.swings };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch weekly swing points');
    }
  }
);

// Fetch chart indicators for a symbol
export const fetchChartIndicators = createAsyncThunk(
  'markets/fetchChartIndicators',
  async ({ symbol, type = null, types = null, combine = true }, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (type) {
        params.append('type', type);
      }
      if (types) {
        params.append('types', types);
      }
      params.append('combine', combine.toString());
      params.append('limit', '100');
      
      const url = `/chart-indicators/${normalizeSymbolForRequest(symbol)}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get(url);
      return { symbol, indicators: response.data.indicators };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch chart indicators');
    }
  }
);

export const fetchIndicatorSettings = createAsyncThunk(
  'markets/fetchIndicatorSettings',
  async (arg, thunkAPI) => {
    try {
      const response = await apiClient.get('/indicator-settings');
      return response.data?.indicators || {};
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch indicator settings');
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { markets } = getState();
      if (markets.indicatorSettings && markets.indicatorSettingsLastFetched > Date.now() - FIVE_MINUTES) {
        return false;
      }
    },
  }
);

export const fetchAssetsByTag = createAsyncThunk(
  'markets/fetchAssetsByTag',
  async (tag, thunkAPI) => {
    try {
      const noSpaceTag = tag.replace(/\s+/g, '-');
      const response = await apiClient.get(`/tags/${noSpaceTag}/assets`);
      return { tag, assets: response.data.assets };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch assets by tag');
    }
  }
);

// Fetch available tags
export const fetchTags = createAsyncThunk(
  'markets/fetchTags',
  async (arg, thunkAPI) => {
    try {
      const response = await apiClient.get('/tags');
      return response.data.tags;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch tags');
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { markets } = getState();
      if (markets.tagsLastFetched && markets.tagsLastFetched > Date.now() - FIVE_MINUTES) {
        return false;
      }
    },
  }
);

// Stable cache key for a set of symbols — order-independent so the same
// watchlist with symbols added/removed in different orders still hits the
// cache when the resulting set is identical. Exported so consumers
// (ScannerWidget) can derive the same key when comparing against
// `scannerLastFetchedSymbolsKey` — the comparison only works if both
// sides use the exact same algorithm.
export const buildScannerSymbolsKey = (symbols) =>
  Array.isArray(symbols) ? [...symbols].sort().join(',') : '';

// Fetch scanner data (prices + day/week % for Market Scanner).
//
// Accepts `{ symbols, force }`. The TTL guard is keyed by the symbol set,
// so a watchlist add/remove always refetches even within the TTL window —
// only requests for the *same* symbol set get short-circuited.
export const fetchScannerData = createAsyncThunk(
  'markets/fetchScannerData',
  async ({ symbols }, { rejectWithValue }) => {
    try {
      // Fetch last 7 days of daily data for each symbol to calc day/week %
      const promises = symbols.map((symbol) =>
        apiClient
          .get(`/market-data/${symbol.replace('/', '-')}?timeframe=daily&limit=7`)
          .then((res) => ({ symbol, candles: res.data?.candles || [] }))
          .catch(() => ({ symbol, candles: [] }))
      );
      const results = await Promise.all(promises);

      // Transform to: { BTC-USD: { price, dayPct, weekPct }, ... }
      const priceData = {};
      results.forEach(({ symbol, candles }) => {
        if (candles.length > 0) {
          const latest = candles[candles.length - 1];
          const prev = candles.length > 1 ? candles[candles.length - 2] : null;
          // Require at least 7 candles for accurate week comparison (daily data)
          const weekAgo = candles.length >= 7 ? candles[0] : null;

          // Handle different candle data formats; explicit null fallback to prevent
          // null coercing to 0 in arithmetic and producing spurious -100% changes
          const latestClose = latest.close ?? latest.c ?? latest.Close ?? null;
          const prevClose = prev?.close ?? prev?.c ?? prev?.Close ?? null;
          const weekAgoClose = weekAgo?.close ?? weekAgo?.c ?? weekAgo?.Close ?? null;

          priceData[symbol] = {
            price: latestClose,
            dayPct:
              latestClose != null && prev && prevClose != null && prevClose !== 0
                ? ((latestClose - prevClose) / prevClose) * 100
                : null,
            weekPct:
              latestClose != null && weekAgo && weekAgoClose != null && weekAgoClose !== 0
                ? ((latestClose - weekAgoClose) / weekAgoClose) * 100
                : null
          };
        }
      });
      return priceData;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { markets } = getState();
      const requestedKey = buildScannerSymbolsKey(arg?.symbols);

      // A fetch for the same symbol set is already in flight — no need
      // to dispatch again. The pending request will commit the right
      // data when it resolves.
      if (markets.scannerInFlightSymbolsKey === requestedKey) {
        return false;
      }

      // We can only trust the fulfilled cache when there's no in-flight
      // fetch for a *different* symbol set about to overwrite it. Without
      // this guard, a user who modifies the watchlist and quickly reverts
      // it would short-circuit on the still-valid cache key, then the
      // pending fetch for the intermediate set would resolve and stomp
      // scannerLastFetchedSymbolsKey with the wrong key — trapping the
      // widget in a permanent loading state with no recovery path.
      const noInFlight = markets.scannerInFlightSymbolsKey === null;
      const cacheMatches = markets.scannerLastFetchedSymbolsKey === requestedKey;
      const cacheFresh =
        markets.scannerLastFetched &&
        markets.scannerLastFetched > Date.now() - TWO_MINUTES;
      if (noInFlight && cacheMatches && cacheFresh) {
        return false;
      }

      // Otherwise: proceed with the fetch.
      return undefined;
    }
  }
);

// Fetch the daily briefing — DWLF's structured intelligence summary
// across the user's watchlist (regime states, recent transitions,
// active custom events, sector sentiment).
//
// Lifted to Redux from BriefingPanel local state so other consumers
// (RegimeHeatmap) can read the same data without a duplicate fetch.
// Single-flight pattern with requestId tracking — same shape as
// fetchScannerData — drops stale responses if multiple calls land
// out of order (e.g. user mashing the refresh button).
export const fetchBriefing = createAsyncThunk(
  'markets/fetchBriefing',
  async (_arg, { rejectWithValue }) => {
    try {
      // depth=1: the weekly cycle depth readings (DWLF-416) in crossAsset.depth.
      const res = await apiClient.get('/briefing/daily', { params: { depth: 1 } });
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to load briefing');
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { markets } = getState();
      // Skip if already in flight or cached within the TTL window.
      if (markets.briefingLoading) return false;
      if (markets.briefingLastFetched && markets.briefingLastFetched > Date.now() - TWO_MINUTES) {
        return false;
      }
      return undefined;
    }
  }
);

const marketsSlice = createSlice({
  name: 'markets',
  initialState: {
    symbols: [],
    // Display names keyed by symbol (XAU/USD → "Gold Spot"); only where the name says more than the ticker.
    symbolNames: {},
    symbolsLastFetched: null,
    tags: [],
    tagsLastFetched: null,
    watchlist: [],
    watchlistLastFetched: null,
    watchlistError: null,
    olderHistory: {}, // { [symbol]: { [timeframe]: { loading, exhausted } } } for fetchOlderCandles (DWLF-426)
    marketData: {}, // Store OHLC data for each symbol - includes lastKnownDate per symbol/timeframe
    assetsByTag: {},
    symbolDetails: {},
    supportResistance: {}, // Store S/R levels for each symbol
    trendlines: {}, // Store trendlines per symbol (keyed by version)
    weeklyTrendlines: {},
    recentTrendlineBreaks: [],
    allRecentEvents: [],
    allRecentEventsLastFetched: null,
    allRecentEventsStatus: 'idle',
    swingPoints: {},
    weeklySwingPoints: {},
    performanceSummary: null,
    performanceSummaryLastFetched: null,
    marketsOverviewLoading: false,
    marketsOverviewLastFetched: null,
    recentlyTriggeredAlerts: [],
    recentlyTriggeredAlertsLoading: false,
    alertsByAsset: {},
    fibLevels: {},
    indicators: {}, // Store chart indicators for each symbol
    indicatorSettings: null,
    indicatorSettingsLastFetched: null,
    indicatorSettingsStatus: 'idle',
    indicatorSettingsError: null,
    symbolsStatus: 'idle',
    symbolsError: null,
    status: 'idle',
    error: null,
    watchlistLoading: false,
    performanceSummaryLoading: false,
    briefingData: null,
    briefingLoading: false,
    briefingError: null,
    briefingLastFetched: null,
    briefingCurrentRequestId: null,
    scannerData: {},
    scannerLoading: false,
    scannerError: null,
    scannerLastFetched: null,
    scannerLastFetchedSymbolsKey: null,
    // Tracks the requestId of the most recent in-flight fetchScannerData
    // dispatch. fulfilled/rejected handlers compare against this so that
    // stale responses (older calls resolving after newer ones — common
    // when the watchlist mutates rapidly, e.g. handleAddDefaults adding
    // symbols sequentially) are dropped instead of stomping correct data.
    scannerCurrentRequestId: null,
    // The symbol-set key currently being fetched. Used by the condition
    // guard so a second dispatch for the same set is short-circuited
    // (already fetching) and a dispatch for a different set is allowed
    // to proceed even if the cache key still matches the requested one.
    scannerInFlightSymbolsKey: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Watchlist (uses own loading state, not global status)
      .addCase(fetchWatchlist.pending, (state) => {
        state.watchlistLoading = true;
        state.watchlistError = null;
      })
      .addCase(fetchWatchlist.fulfilled, (state, action) => {
        state.watchlistLoading = false;
        state.watchlist = action.payload;
        state.watchlistLastFetched = Date.now();
        state.watchlistError = null;
      })
      .addCase(fetchWatchlist.rejected, (state, action) => {
        state.watchlistLoading = false;
        state.watchlistError = action.payload ?? 'Failed to fetch watchlist';
      })
      // ✅ Add to Watchlist
      .addCase(addToWatchlist.fulfilled, (state, action) => {
        if (!state.watchlist.includes(action.payload)) {
          state.watchlist.push(action.payload);
        }
      })
      // ✅ Remove from Watchlist
      .addCase(removeFromWatchlist.fulfilled, (state, action) => {
        const normalizedPayload = action.payload.replace('/', '-');
        state.watchlist = state.watchlist.filter((symbol) => symbol.replace('/', '-') !== normalizedPayload);
      })

      // Market Symbols
      .addCase(fetchMarketSymbols.pending, (state) => {
        state.symbolsStatus = 'loading';
        state.symbolsError = null;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchMarketSymbols.fulfilled, (state, action) => {
        state.symbolsStatus = 'succeeded';
        state.status = 'succeeded';
        state.symbols = action.payload.map((item) => item.symbol);
        state.symbolNames = Object.fromEntries(action.payload
          .filter((item) => item.name && item.name !== item.symbol)
          .map((item) => [item.symbol, item.name]));
        state.symbolsLastFetched = Date.now();
      })
      .addCase(fetchMarketSymbols.rejected, (state, action) => {
        state.symbolsStatus = 'failed';
        state.symbolsError = action.payload;
        state.status = 'failed';
        state.error = action.payload;
      })

      // Market Data (OHLC) - Cached loading
      .addCase(loadCachedMarketData.fulfilled, (state, action) => {
        if (!action.payload) return; // No cached data found
        
        const { symbol, candles, timeframe, meta, cacheTimestamp } = action.payload;
        
        // Initialize market data structure if needed
        if (!state.marketData[symbol]) {
          state.marketData[symbol] = {
            byTimeframe: {},
            lastKnownDate: {}
          };
        }
        
        // Store cached candles in byTimeframe structure
        state.marketData[symbol].byTimeframe[timeframe] = candles;
        
        // Also set legacy candles and timeframe fields that components depend on
        // For daily timeframe or if no legacy data exists, update legacy fields
        if (timeframe === 'daily' || !state.marketData[symbol].candles) {
          state.marketData[symbol].candles = candles;
          state.marketData[symbol].timeframe = timeframe;
        }
        
        // Track last known date for incremental fetching
        if (candles.length > 0) {
          const lastCandle = candles[candles.length - 1];
          state.marketData[symbol].lastKnownDate[timeframe] = resolveTimestampFromCandle(lastCandle);
        }
        
        // Mark as loaded from cache for UI feedback
        state.marketData[symbol].isFromCache = true;
        state.marketData[symbol].cacheTimestamp = cacheTimestamp;
      })

      // Market Data (OHLC) - API loading
      .addCase(fetchMarketData.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchMarketData.fulfilled, (state, action) => {
        const { symbol, candles, timeframe, meta, isIncremental } = action.payload;
        const existing = state.marketData[symbol] || {};
        
        // Merge candles if this is an incremental fetch
        let finalCandles = candles;
        const existingCandles = existing.byTimeframe?.[timeframe];
        if (isIncremental && Array.isArray(existingCandles) && Array.isArray(candles)) {
          // Use shared merge function to maintain consistency with cache layer
          finalCandles = mergeCandles(existingCandles, candles);
        }
        
        // Calculate the last known date from the final candles
        const lastKnownDate = existing.lastKnownDate || {};
        if (Array.isArray(finalCandles) && finalCandles.length > 0) {
          const lastCandle = finalCandles[finalCandles.length - 1];
          const lastTimestamp = resolveTimestampFromCandle(lastCandle);
          if (lastTimestamp !== null) {
            lastKnownDate[timeframe] = lastTimestamp;
          }
        }
        
        const byTimeframe = {
          ...(existing.byTimeframe || {}),
          [timeframe]: finalCandles,
        };
        const metaByTimeframe = {
          ...(existing.metaByTimeframe || {}),
          [timeframe]: meta,
        };

        const legacyCandles =
          timeframe === 'daily' || !Array.isArray(existing.candles) || existing.candles.length === 0
            ? finalCandles
            : existing.candles;

        const legacyTimeframe =
          timeframe === 'daily'
            ? 'daily'
            : existing.timeframe || 'daily';

        state.marketData[symbol] = {
          ...existing,
          candles: legacyCandles,
          timeframe: legacyTimeframe,
          latestTimeframe: timeframe,
          byTimeframe,
          metaByTimeframe,
          lastKnownDate,
          lastUpdatedAt: Date.now(),
          // Clear cache flags when fresh API data arrives
          isFromCache: false,
          cacheTimestamp: undefined,
        };
        state.status = 'succeeded';
      })
      .addCase(fetchMarketData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Older candles, prepended when the chart is panned back (DWLF-426)
      .addCase(fetchOlderCandles.pending, (state, action) => {
        const { symbol, timeframe } = action.meta.arg;
        state.olderHistory[symbol] = state.olderHistory[symbol] || {};
        state.olderHistory[symbol][timeframe] = { ...state.olderHistory[symbol][timeframe], loading: true };
      })
      .addCase(fetchOlderCandles.fulfilled, (state, action) => {
        const { symbol, timeframe, candles } = action.payload;
        // Absent when the store was reset (sign-out) while the request was in flight.
        const history = state.olderHistory[symbol]?.[timeframe];
        if (!history) return;
        history.loading = false;
        if (candles.length === 0) {
          history.exhausted = true;
          return;
        }
        const entry = state.marketData[symbol];
        const held = entry?.byTimeframe?.[timeframe];
        if (!Array.isArray(held)) return;
        const merged = mergeCandles(held, candles);
        // A page that reaches no further back (a route that ignored `before`) ends paging too,
        // or every pan near the edge would ask for the same page again.
        if (!(resolveTimestampFromCandle(merged[0]) < resolveTimestampFromCandle(held[0]))) {
          history.exhausted = true;
        }
        entry.byTimeframe[timeframe] = merged;
        if (entry.timeframe === timeframe) {
          entry.candles = merged;
        }
      })
      .addCase(fetchOlderCandles.rejected, (state, action) => {
        if (action.meta.condition) return;
        const { symbol, timeframe } = action.meta.arg;
        const history = state.olderHistory[symbol]?.[timeframe];
        if (history) history.loading = false;
      })

      // Support & Resistance
      .addCase(fetchSupportResistance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSupportResistance.fulfilled, (state, action) => {
        const { symbol, timeframe = 'daily', levels } = action.payload;
        const normalizedTimeframe =
          typeof timeframe === 'string' ? timeframe.toLowerCase() : 'daily';
        if (!state.supportResistance[symbol]) {
          state.supportResistance[symbol] = {};
        }
        state.supportResistance[symbol][normalizedTimeframe] = levels;
        state.status = 'succeeded';
      })
      .addCase(fetchSupportResistance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Trendlines
      .addCase(fetchTrendlines.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTrendlines.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const {
          symbol,
          version,
          trendlines,
          timeframe = 'daily',
        } = action.payload;

        if (!symbol || version === undefined) {
          return;
        }

        const existing = state.trendlines[symbol] || {};
        const existingByTimeframe = existing.byTimeframe || {};
        const timeframeEntry = existingByTimeframe[timeframe] || {};

        const versions = {
          ...(timeframeEntry.versions || {}),
          [version]: trendlines,
        };

        state.trendlines[symbol] = {
          ...existing,
          latestTimeframe: timeframe,
          byTimeframe: {
            ...existingByTimeframe,
            [timeframe]: {
              ...timeframeEntry,
              versions,
              latestVersion: version,
              lastUpdatedAt: Date.now(),
            },
          },
        };
      })
      .addCase(fetchTrendlines.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Weekly Trendlines
      .addCase(fetchWeeklyTrendlines.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchWeeklyTrendlines.fulfilled, (state, action) => {
        const { symbol, version = DEFAULT_TRENDLINE_VERSION, trendlines } = action.payload;
        if (!symbol || version === undefined) {
          return;
        }

        const existing = state.weeklyTrendlines[symbol] || {};
        const versions = {
          ...(existing.versions || {}),
          [version]: trendlines,
        };

        state.weeklyTrendlines[symbol] = {
          ...existing,
          versions,
          latestVersion: version,
          lastUpdatedAt: Date.now(),
        };
        state.status = 'succeeded';
      })
      .addCase(fetchWeeklyTrendlines.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Trendline Breaks
      .addCase(fetchRecentTrendlineBreaks.fulfilled, (state, action) => {
        state.recentTrendlineBreaks = action.payload;
      })
      .addCase(fetchRecentTrendlineBreaks.rejected, (state, action) => {
        state.error = action.payload;
      })
      // All Recent Events
      .addCase(fetchAllRecentEvents.pending, (state) => {
        state.allRecentEventsStatus = 'loading';
      })
      .addCase(fetchAllRecentEvents.fulfilled, (state, action) => {
        state.allRecentEvents = action.payload;
        state.allRecentEventsLastFetched = Date.now();
        state.allRecentEventsStatus = 'idle';
      })
      .addCase(fetchAllRecentEvents.rejected, (state, action) => {
        state.error = action.payload;
        state.allRecentEventsStatus = 'idle';
      })
      
      // Swing Points
      .addCase(fetchSwingPoints.fulfilled, (state, action) => {
        const { symbol, swings, timeframe = 'daily' } = action.payload;
        const existing = state.swingPoints[symbol] || {};
        const byTimeframe = {
          ...(existing.byTimeframe || {}),
          [timeframe]: swings,
        };

        state.swingPoints[symbol] = {
          ...existing,
          byTimeframe,
          latestTimeframe: timeframe,
          lastUpdatedAt: Date.now(),
        };
        state.status = 'succeeded';
      })
      .addCase(fetchSwingPoints.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(fetchSymbolDetails.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSymbolDetails.fulfilled, (state, action) => {
        const { symbol, details } = action.payload;
        state.symbolDetails[symbol] = details;
        state.status = 'succeeded';
      })
      .addCase(fetchSymbolDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Fetch Performance Summary
      .addCase(fetchPerformanceSummary.pending, (state) => {
        state.status = 'loading';
        state.performanceSummaryLoading = true;
      })
      .addCase(fetchPerformanceSummary.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.performanceSummaryLoading = false;
        state.performanceSummary = action.payload;
        state.performanceSummaryLastFetched = Date.now();
      })
      .addCase(fetchPerformanceSummary.rejected, (state, action) => {
        state.status = 'failed';
        state.performanceSummaryLoading = false;
        state.error = action.payload;
      })

      // Combined markets overview
      .addCase(fetchMarketsOverview.pending, (state) => {
        state.marketsOverviewLoading = true;
        state.performanceSummaryLoading = true;
        state.watchlistLoading = true;
      })
      .addCase(fetchMarketsOverview.fulfilled, (state, action) => {
        state.marketsOverviewLoading = false;
        state.marketsOverviewLastFetched = Date.now();
        const { tags, performanceSummary, watchlist } = action.payload || {};
        if (Array.isArray(tags)) {
          state.tags = tags;
          state.tagsLastFetched = Date.now();
        }
        if (performanceSummary) {
          state.performanceSummary = performanceSummary;
          state.performanceSummaryLastFetched = Date.now();
        }
        state.performanceSummaryLoading = false;
        if (Array.isArray(watchlist)) {
          state.watchlist = watchlist;
          // Only stamp watchlistLastFetched when we actually received
          // valid watchlist data — otherwise the standalone fetchWatchlist
          // fallback gets blocked for 2 minutes and BriefingPanel shows
          // a false "empty watchlist" CTA.
          state.watchlistLastFetched = Date.now();
          state.watchlistError = null;
        }
        state.watchlistLoading = false;
      })
      .addCase(fetchMarketsOverview.rejected, (state, action) => {
        state.marketsOverviewLoading = false;
        state.performanceSummaryLoading = false;
        state.watchlistLoading = false;
        state.error = action.payload;
        // Set watchlistError (NOT watchlistLastFetched) so BriefingPanel's
        // watchlistReady guard resolves without blocking the standalone
        // fetchWatchlist retry path. Setting watchlistLastFetched here
        // would block retries for 2 minutes AND trigger the false "empty
        // watchlist" CTA since watchlist stays at [].
        state.watchlistError = action.payload ?? 'Markets overview failed';
      })

      // Weekly Swing Points
      .addCase(fetchWeeklySwingPoints.fulfilled, (state, action) => {
        const { symbol, swings } = action.payload;
        state.weeklySwingPoints[symbol] = swings;
      })
      .addCase(fetchWeeklySwingPoints.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(fetchTags.fulfilled, (state, action) => {
        state.tags = action.payload;
        state.tagsLastFetched = Date.now();
        state.status = 'succeeded';
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchAssetsByTag.fulfilled, (state, action) => {
        state.assetsByTag[action.payload.tag] = action.payload.assets;
        state.status = 'succeeded';
      })
      .addCase(fetchAssetsByTag.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchRecentlyTriggeredAlerts.pending, (state) => {
        state.recentlyTriggeredAlertsLoading = true;
      })
      .addCase(fetchRecentlyTriggeredAlerts.fulfilled, (state, action) => {
        state.recentlyTriggeredAlerts = action.payload;
        state.recentlyTriggeredAlertsLoading = false;
      })
      .addCase(fetchRecentlyTriggeredAlerts.rejected, (state, action) => {
        state.error = action.payload;
        state.recentlyTriggeredAlertsLoading = false;
      })
      .addCase(fetchAlertsByAsset.fulfilled, (state, action) => {
        const { symbol, alerts } = action.payload;
        state.alertsByAsset[symbol] = alerts;
      })
      .addCase(fetchAlertsByAsset.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchSystemFibLevels.fulfilled, (state, action) => {
        const { symbol, level } = action.payload;
        state.fibLevels[symbol] = level ? [level] : [];
        state.status = 'succeeded';
      })
      .addCase(fetchIndicatorSettings.pending, (state) => {
        state.indicatorSettingsStatus = 'loading';
        state.indicatorSettingsError = null;
      })
      .addCase(fetchIndicatorSettings.fulfilled, (state, action) => {
        state.indicatorSettingsStatus = 'succeeded';
        state.indicatorSettings = action.payload;
        state.indicatorSettingsLastFetched = Date.now();
      })
      .addCase(fetchIndicatorSettings.rejected, (state, action) => {
        state.indicatorSettingsStatus = 'failed';
        state.indicatorSettingsError = action.payload;
      })
      .addCase(fetchChartIndicators.fulfilled, (state, action) => {
        const { symbol, indicators } = action.payload;
        state.indicators[symbol] = indicators;
        state.status = 'succeeded';
      })
      .addCase(fetchChartIndicators.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // loadMarketData – correct status after cache-first orchestration
      .addCase(loadMarketData.fulfilled, (state, action) => {
        if (action.payload) {
          state.status = 'succeeded';
          state.error = null;
        }
      })
      .addCase(loadMarketData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Briefing
      .addCase(fetchBriefing.pending, (state, action) => {
        state.briefingLoading = true;
        state.briefingError = null;
        state.briefingCurrentRequestId = action.meta.requestId;
      })
      .addCase(fetchBriefing.fulfilled, (state, action) => {
        // Drop stale responses — only commit if this is still the
        // latest in-flight request.
        if (state.briefingCurrentRequestId !== action.meta.requestId) {
          return;
        }
        state.briefingLoading = false;
        state.briefingError = null;
        state.briefingData = action.payload;
        state.briefingLastFetched = Date.now();
        state.briefingCurrentRequestId = null;
      })
      .addCase(fetchBriefing.rejected, (state, action) => {
        if (state.briefingCurrentRequestId !== action.meta.requestId) {
          return;
        }
        state.briefingLoading = false;
        state.briefingError = action.payload ?? 'Failed to load briefing';
        state.briefingCurrentRequestId = null;
      })

      // Scanner Data
      .addCase(fetchScannerData.pending, (state, action) => {
        state.scannerLoading = true;
        state.scannerError = null;
        // Mark this as the latest in-flight request. Any earlier
        // requests that resolve later will be silently dropped.
        state.scannerCurrentRequestId = action.meta.requestId;
        state.scannerInFlightSymbolsKey = buildScannerSymbolsKey(action.meta.arg?.symbols);
      })
      .addCase(fetchScannerData.fulfilled, (state, action) => {
        // Drop stale responses — only commit if this is still the
        // latest in-flight request. Without this, an older fetch
        // resolving after a newer one would stomp correct data and
        // leave scannerLastFetchedSymbolsKey mismatched, trapping the
        // widget in a stuck loading state.
        if (state.scannerCurrentRequestId !== action.meta.requestId) {
          return;
        }
        state.scannerLoading = false;
        state.scannerError = null;
        state.scannerData = action.payload;
        state.scannerLastFetched = Date.now();
        // Record the symbol set this fetch was for, so the next call's
        // condition guard can decide whether the cache is reusable.
        state.scannerLastFetchedSymbolsKey = buildScannerSymbolsKey(action.meta.arg?.symbols);
        state.scannerCurrentRequestId = null;
        state.scannerInFlightSymbolsKey = null;
      })
      .addCase(fetchScannerData.rejected, (state, action) => {
        if (state.scannerCurrentRequestId !== action.meta.requestId) {
          return;
        }
        state.scannerLoading = false;
        state.scannerError = action.payload;
        state.scannerCurrentRequestId = null;
        state.scannerInFlightSymbolsKey = null;
      });
  },
});

//export { addToWatchlist, removeFromWatchlist };

// resolveTimestampFromCandle is now exported from utils/candleUtils.js

export default marketsSlice.reducer;
